/**
 * Holds shopper identity: the auth user, the Loot session token, and the shared
 * LootClient. Everything else (balance, balance packs, purchases) lives in the React
 * Query cache via queries.ts. All Loot traffic flows through the BFF proxy
 * (same-origin /api).
 */
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo } from "react";

import { settleAnalyticsIdentity } from "../lib/analytics.js";
import { authMeQueryOptions, lootClient, qk, sessionQueryOptions } from "../lib/queries.js";
import { queryClient } from "../lib/queryClient.js";
import { ShopperContext, type ShopperContextValue } from "../lib/shopper.js";
import { setRealtimeViewer } from "../lib/useBattleChannel.js";

/** Provides shopper identity to the app; mounted once, above the router. */
export function ShopperProvider({ children }: { children?: ReactNode }) {
  const authQuery = useQuery(authMeQueryOptions());
  const user = authQuery.data ?? null;
  const authPending = authQuery.isPending;

  // Shopper id only; a Sentry event never says which email opened a box.
  useEffect(() => {
    Sentry.setUser(user ? { id: user.id } : null);
  }, [user]);

  // me → session (→ balance, in useBalance): each link enables the next.
  const sessionQuery = useQuery({ ...sessionQueryOptions(), enabled: !!user });
  const sessionToken = sessionQuery.data?.token ?? null;
  // True from the moment a user is known until the session token settles (minted or failed), so a
  // consumer can wait for the token instead of fetching a token-scoped view (like a battle's `me`)
  // as an anonymous spectator during the two-step auth → session handoff.
  const sessionPending = !!user && sessionQuery.isPending;

  // The tracker records nothing until it can send under the shopper's bearer (or knows there is
  // none), so a signed-in reload's landing view is the shopper's and not an anonymous browser's.
  useEffect(() => {
    if (!authPending && !sessionPending) settleAnalyticsIdentity();
  }, [authPending, sessionPending]);

  // The realtime token is minted under the session once there is one, and names the shopper.
  const realtimeViewer = user && sessionToken ? user.id : "anon";
  useEffect(() => setRealtimeViewer(realtimeViewer), [realtimeViewer]);

  // Signed out (initially, after sign-out, or a lost session): drop the token and its cache.
  useEffect(() => {
    if (authPending || user) return;
    lootClient.setSessionToken(null);
    queryClient.removeQueries({ queryKey: qk.private });
  }, [authPending, user]);

  const value = useMemo<ShopperContextValue>(
    () => ({ user, authPending, sessionPending, sessionToken, client: lootClient }),
    [user, authPending, sessionPending, sessionToken],
  );

  return <ShopperContext.Provider value={value}>{children}</ShopperContext.Provider>;
}
